
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";

import enUK from "@/locales/invoicing/en-UK.json";

import { GET_OPENED_INVOICES } from "@/graphql/invoice/queries";

import {
  InvoiceCollectionStatus,
  InvoicingRole,
  LateFilterOptions,
} from "@/helpers/invoice/typesDefinition";
import { useToast } from "vue-toastification";
import { useQuery } from "@vue/apollo-composable";
import { fullName } from "@/helpers/user/utils";
import { User } from "@/helpers/user/typesDefinition";
import BaseSheet from "@/BaseSheet.vue";
import { Icon } from "@iconify/vue";
import MainContentBase from "@/components/layout/MainContentBase.vue";
import { GET_USERS_BY_ROLES } from "@/graphql/user/queries";
import { logErrorMessages } from "@vue/apollo-util";
import { GET_ALL_ACTIVE_CLIENTS } from "@/graphql/client/queries";
import { Client } from "@/helpers/client/typesDefinition";
import InvoicesList from "@/views/invoicing/InvoicesList.vue";

type MessageSchema = typeof enUK;

export default defineComponent({
  setup() {
    const { t } = useI18n<[MessageSchema], "en-UK">({
      useScope: "global",
      inheritLocale: true,
      messages: {
        "en-UK": enUK,
      },
    });

    const filterAssignedToInput = ref();
    const filterCollectionStatusInput = ref();
    const filterClientsInput = ref();
    const loading = ref(false);

    const { result: getUsersResult, onError: onGetUsersError } = useQuery(
      GET_USERS_BY_ROLES,
      { roles: [InvoicingRole.ADMIN] },
    );

    onGetUsersError((error) => {
      logErrorMessages(error);
      useToast().error(t("errors.getUsersByRole"));
    });

    const usersList = computed(() => getUsersResult.value?.users ?? []);
    const filterAssignedToOptions = computed(() => {
      return usersList.value
        .filter((user: User) => !!user.firstName)
        .reduce((acc: { [key: string]: string }, user: User) => {
          acc[user.id] = fullName(user);
          return acc;
        }, {});
    });

    const filterCollectionStatusOptions = {
      [InvoiceCollectionStatus.ABANDONED]: t(
        "collectionStatus." + InvoiceCollectionStatus.ABANDONED,
      ),
      [InvoiceCollectionStatus.DOUBTFUL]: t(
        "collectionStatus." + InvoiceCollectionStatus.DOUBTFUL,
      ),
      [InvoiceCollectionStatus.CREDIT_NOTE_TO_ISSUE]: t(
        "collectionStatus." + InvoiceCollectionStatus.CREDIT_NOTE_TO_ISSUE,
      ),
    };

    const { result: getClientsResult, onError: onGetClientsError } = useQuery(
      GET_ALL_ACTIVE_CLIENTS,
    );
    onGetClientsError((error) => {
      logErrorMessages(error);
      useToast().error(t("errors.getClients"));
    });

    const clientsList = computed(
      () => getClientsResult.value?.activeClients ?? [],
    );
    const filterClientsOptions = computed(() => {
      return clientsList.value
        .filter((client: Client) => !!client.name && !!client.id)
        .reduce((acc: { [key: string]: string }, client: Client) => {
          acc[client.id] = client.name!;
          return acc;
        }, {});
    });

    const filteringInput = computed(() => {
      return {
        ...(filterAssignedToInput.value
          ? { assignedToUser: filterAssignedToInput.value }
          : {}),
        ...(filterClientsInput.value
          ? { client: filterClientsInput.value }
          : {}),
      };
    });

    const filteringInput_includingAllCollectionStatus = computed(() => {
      return {
        ...filteringInput.value,
        ...(filterCollectionStatusInput.value
          ? { collectionStatus: [filterCollectionStatusInput.value] }
          : {}),
      };
    });

    const filteringInput_withOnlyCompromisedPayment = computed(() => {
      return {
        ...filteringInput.value,
        ...(filterCollectionStatusInput.value
          ? { collectionStatus: [filterCollectionStatusInput.value] }
          : {
              collectionStatus: [
                InvoiceCollectionStatus.CREDIT_NOTE_TO_ISSUE,
                InvoiceCollectionStatus.ABANDONED,
                InvoiceCollectionStatus.DOUBTFUL,
              ],
            }),
      };
    });

    const refetchQueries = computed(() => [
      {
        query: GET_OPENED_INVOICES,
        variables: {
          lateFilter: LateFilterOptions.DUE_AND_PROMISED_DATE_LATE,
          assignedToRole: [InvoicingRole.ADMIN],
          collectionStatus: [InvoiceCollectionStatus.EXPECTED_PAYMENT],
          offset: 0,
          ...filteringInput.value,
        },
      },
      {
        query: GET_OPENED_INVOICES,
        variables: {
          lateFilter: LateFilterOptions.DUE_AND_PROMISED_DATE_LATE,
          assignedToRole: [InvoicingRole.ADMIN],
          offset: 0,
          ...filteringInput_withOnlyCompromisedPayment.value,
        },
      },
      {
        query: GET_OPENED_INVOICES,
        variables: {
          lateFilter: LateFilterOptions.DELAYED,
          assignedToRole: [InvoicingRole.ADMIN],
          offset: 0,
          ...filteringInput_includingAllCollectionStatus.value,
        },
      },
      {
        query: GET_OPENED_INVOICES,
        variables: {
          assignedToRole: [InvoicingRole.FINANCIAL],
          offset: 0,
          ...filteringInput_includingAllCollectionStatus.value,
        },
      },
    ]);

    const areExpectedPaymentsVisible = ref(true);
    const switchAreExpectedPaymentsVisible = () => {
      areExpectedPaymentsVisible.value = !areExpectedPaymentsVisible.value;
    };
    const areCompromisedInvoicesVisible = ref(false);
    const switchAreCompromisedInvoicesVisible = () => {
      areCompromisedInvoicesVisible.value =
        !areCompromisedInvoicesVisible.value;
    };
    const areDelayedInvoicesVisible = ref(false);
    const switchAreDelayedInvoicesVisible = () => {
      areDelayedInvoicesVisible.value = !areDelayedInvoicesVisible.value;
    };
    const areFOInvoicesVisible = ref(false);
    const switchAreFOInvoicesVisible = () => {
      areFOInvoicesVisible.value = !areFOInvoicesVisible.value;
    };

    return {
      t,
      filteringInput,
      filteringInput_withOnlyCompromisedPayment,
      filteringInput_includingAllCollectionStatus,
      refetchQueries,
      loading,
      areExpectedPaymentsVisible,
      switchAreExpectedPaymentsVisible,
      areCompromisedInvoicesVisible,
      switchAreCompromisedInvoicesVisible,
      areDelayedInvoicesVisible,
      switchAreDelayedInvoicesVisible,
      areFOInvoicesVisible,
      switchAreFOInvoicesVisible,
      filterAssignedToOptions,
      filterAssignedToInput,
      filterCollectionStatusOptions,
      filterCollectionStatusInput,
      filterClientsInput,
      filterClientsOptions,
      usersList,
    };
  },
  data() {
    return {
      enUK,
      GET_OPENED_INVOICES,
      isEditModalVisible: false,
      InvoicingRole,
      InvoiceCollectionStatus,
      LateFilterOptions,
    };
  },
  emits: [],
  provide: {
    columnsOrder: {
      invoiceNumber: null,
      client: null,
      company: null,
      total: null,
      paid: null,
      balance: null,
      dueDate: null,
      nbReminders: null,
      promisedDate: null,
      communication: null,
      comment: null,
      paymentOption: null,
      collectionStatus: null,
      assignedTo: null,
    },
  },
  components: {
    InvoicesList,
    MainContentBase,
    Icon,
    BaseSheet,
  },
});
